import {type Plan} from 'client';

import {type IDateRange, type SupportedLanguages} from 'interfaces';
import {dateReader, localDateSender} from 'helpers/UnitsFormatHandler';
import {format, formatDistance, type Locale, add} from 'date-fns';
import {nb, enGB} from 'date-fns/locale';

export abstract class DateFormater {
  constructor(readonly locale: Locale) {}

  abstract toDateString(date: string | Date): string;
  abstract toDateTimeString(date: string | Date): string;
  toFormat(date: string | Date, format_: string): string {
    date = assertDate(date);
    return format(date, format_, {locale: this.locale});
  }

  toWeekYearString(date: string | Date): string {
    date = assertDate(date);
    return format(date, 'I/yyyy');
  }

  toWeek(date: string | Date): string {
    date = assertDate(date);
    return format(date, 'I');
  }

  toLongDateString(date: string | Date): string {
    date = assertDate(date);
    return format(date, 'PP', {locale: this.locale});
  }

  toLimitedTimeAgo(date: string | Date): string {
    if (date === null || date === undefined) {
      return '';
    }

    const realDate = assertDate(date);
    const now = new Date();
    const milliseconds = now.getTime() - realDate.getTime();
    const hours = milliseconds / 1000 / 60 / 60;
    if (hours <= 24) {
      return formatDistance(realDate, now, {locale: this.locale, addSuffix: true});
    }
    return this.toDateTimeString(realDate);
  }

  toTimeAgo(date: string | Date): string {
    const realDate = assertDate(date);
    const now = new Date();
    return formatDistance(realDate, now, {locale: this.locale, addSuffix: true});
  }

  toWeekDayName(date: string | Date): string {
    date = assertDate(date);
    return format(date, 'eeee', {locale: this.locale});
  }

  abstract getDateFormat(): string;
  abstract getDateFormatMask(): string;
}

class NorwegianDateFormater extends DateFormater {
  toDateString(date: string | Date): string {
    date = assertDate(date);
    return format(date, 'dd.MM.yyyy');
  }

  toDateTimeString(date: string | Date): string {
    date = assertDate(date);
    return format(date, 'dd.MM.yyyy HH:mm');
  }

  getDateFormat() {
    return 'dd.MM.yyyy';
  }

  getDateFormatMask(): string {
    return '__.__.____';
  }
}

class EnglishDateFormater extends DateFormater {
  toDateString(date: string | Date): string {
    date = assertDate(date);
    return format(date, 'yyyy-MM-dd');
  }

  toDateTimeString(date: string | Date): string {
    date = assertDate(date);
    return format(date, 'yyyy-MM-dd HH:mm');
  }

  getDateFormat() {
    return 'yyyy-MM-dd';
  }

  getDateFormatMask(): string {
    return '____.__.__';
  }
}

const englishLanguageSpport = new EnglishDateFormater(enGB);
const norwegianLanguageSpport = new NorwegianDateFormater(nb);

export const getDateFormater = (language?: SupportedLanguages): DateFormater => {
  let selectedLanguage: string | undefined = language;
  if (selectedLanguage === undefined) {
    selectedLanguage = localStorage.getItem('selectedLanguage') ?? 'en';
  }
  switch (selectedLanguage) {
    case 'no':
      return norwegianLanguageSpport;
    case 'en':
    default:
      return englishLanguageSpport;
  }
};

const assertDate = (date: string | Date): Date => {
  if (typeof date === 'string') {
    return dateReader(date);
  }
  return date;
};

const toDateString = (date: string | Date) => {
  const languageSupport = getDateFormater();
  return languageSupport.toDateString(date);
};

const toWeekYearString = (date: string | Date) => {
  const languageSupport = getDateFormater();
  return languageSupport.toWeekYearString(date);
};

const toDateTimeString = (date: string | Date) => {
  const languageSupport = getDateFormater();
  return languageSupport.toDateTimeString(date);
};

const toAgo = (date: string | Date) => {
  const languageSupport = getDateFormater();
  return languageSupport.toLimitedTimeAgo(date);
};

const toWeek = (date: string | Date) => {
  const languageSupport = getDateFormater();
  return languageSupport.toWeek(date);
};

const toLongDateString = (date: string | Date) => {
  const languageSupport = getDateFormater();
  return languageSupport.toLongDateString(date);
};

const toLimitedTimeAgo = (date: string | Date) => {
  const languageSupport = getDateFormater();
  return languageSupport.toLimitedTimeAgo(date);
};

const toWeekdayName = (date: string | Date) => {
  const languageSupport = getDateFormater();
  return languageSupport.toWeekDayName(date);
};

export const dateDisplay = (date: string | Date): string => {
  return toDateString(date);
};

export const weekDisplay = (date: string | Date) => {
  return toWeek(date);
};

const getLocaleDateFormat = (): string => {
  const languageSupport = getDateFormater();
  return languageSupport.getDateFormat();
};

export const determineWholeDateRangeForPlans = (plans: Plan[]) => {
  if (!plans.length) {
    return;
  }
  let startDate: Date = dateReader(plans[0].startDate);
  let endDate: Date = dateReader(plans[0].endDate);

  for (const plan of plans) {
    const planStartDate = dateReader(plan.startDate);
    const planEndDate = dateReader(plan.endDate);

    startDate =
      (startDate && startDate.getTime() > planStartDate.getTime() ? planStartDate : startDate) || planStartDate;
    endDate = (endDate && endDate.getTime() < planEndDate.getTime() ? planEndDate : endDate) || planEndDate;
  }

  return {
    startDate: startDate && localDateSender(startDate),
    endDate: endDate && localDateSender(endDate),
  };
};

export const getDateRanges = (periodStart: Date, periodEnd: Date) => {
  const years: IDateRange[] = [
    {
      startDate: periodStart,
      endDate: new Date(periodStart.getFullYear(), 11, 31),
    },
  ];

  const diffYears = periodEnd.getFullYear() - periodStart.getFullYear();

  for (let i = 1; i < diffYears; i++) {
    const tmp: IDateRange = {
      startDate: new Date(),
      endDate: new Date(),
    };

    tmp.startDate = new Date(periodStart.getFullYear() + i, 0, 1);
    tmp.endDate = new Date(periodStart.getFullYear() + i, 11, 31);
    years.push(tmp);
  }

  years.push({
    startDate: new Date(periodEnd.getFullYear(), 0, 1),
    endDate: periodEnd,
  });

  return years;
};

const isToday = (date: Date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export function secondsFormat(duration) {
  // Hours, minutes and seconds
  const hrs = Math.floor(duration / 3600);
  const mins = Math.floor((duration % 3600) / 60);
  const secs = Math.floor(duration % 60);

  let ret = '';
  if (hrs > 0) {
    ret += '' + hrs + ':' + (mins < 10 ? '0' : '');
  }
  ret += '' + mins + ':' + (secs < 10 ? '0' : '');
  ret += '' + secs;
  return ret;
}

const addDayDelta = (isoDateString: string, days: number) => {
  const date = dateReader(isoDateString);
  return add(date, {days}).toISOString().split('T')[0];
};

export const isDateBetween = (
  date: string | Date,
  startDate: string | Date,
  endDate: string | Date,
  inclusive = true,
): boolean => {
  const checkDate = assertDate(date).getTime();
  const start = assertDate(startDate).getTime();
  const end = assertDate(endDate).getTime();

  return inclusive ? checkDate >= start && checkDate <= end : checkDate > start && checkDate < end;
};
